import { DialogRoot } from "@radix-ui/themes";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../../hooks/useFetch";
import { Event, Reason, SelectOption } from "../../../../../../types/types";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../../../../ui/Dialog";
import { Label } from "../../../../../../ui/Label/Label";
import { Button } from "../../../../../../ui/Button";
import OneSelector from "../../../../../../ui/OneSelector/OneSelector";
import DeleteReason from "./DeleteReason";
import { DateTime } from "luxon";

type Props = {
  equipmentId: string;
  data: { reason: Reason | undefined; idle: Event } | undefined;
  downtimes: SelectOption[];
  isEditOpen: boolean;
  closeModal: Function;
  refreshChanges: Function;
};

const EditLossesReasonsModal = ({
  equipmentId,
  data,
  downtimes,
  isEditOpen,
  closeModal,
  refreshChanges,
}: Props) => {
  const reason = data?.reason;
  const idle = data?.idle!;
  const [newReason, setNewReason] = useState<{
    value: string;
    label: string;
  }>();
  const { register, reset, handleSubmit, control, getValues, setValue } =
    useForm({
      defaultValues: {
        reason: reason
          ? { value: reason.id, label: reason.downtime_reason }
          : undefined,
      },
    });

  useEffect(() => {
    setValue(
      "reason",
      reason ? { value: reason.id, label: reason.downtime_reason } : undefined,
    );
  }, [reason?.downtime_reason]);

  const openStateChange = () => {
    if (isEditOpen) {
      reset();
      closeModal();
    }
  };

  const [
    {
      response: itemCreateResponse,
      isLoading: itemUpdateIsLoading,
      error: itemUpdateError,
    },
    doFetchItemCreate,
  ] = useFetch(`/directories/equipment_items/${equipmentId}/changes`);

  const [
    {
      response: deleteResponse,
      isLoading: deleteIsLoading,
      error: deleteError,
    },
    doFetchItemDelete,
  ] = useFetch(
    `/directories/equipment_items/${equipmentId}/changes/${data?.reason?.id}`,
  );

  const createDowntimeReason = (reasonId: any) => {
    if (!idle) return;
    doFetchItemCreate({
      method: "POST",
      data: {
        data: {
          at: DateTime.fromSeconds(Number(idle[4])).toISO(),
          downtime_reason_id: reasonId,
        },
      },
    });
  };

  const onSubmit = (formData: any) => {
    setNewReason(formData.reason);
    if (reason)
      doFetchItemDelete({
        method: "DELETE",
      });
    else createDowntimeReason(formData.reason.value);
  };

  useEffect(() => {
    if (!deleteResponse) return;
    createDowntimeReason(
      downtimes.find((s) => s.label == String(newReason?.label))?.value,
    );
  }, [deleteResponse]);

  useEffect(() => {
    if (itemCreateResponse) {
      refreshChanges();
      openStateChange();
    }
  }, [itemCreateResponse]);

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  return (
    <DialogRoot open={isEditOpen} onOpenChange={openStateChange}>
      <DialogContent style={{ maxWidth: 600, position: "fixed", top: "30%" }}>
        <DialogTitle>
          <span className="text-lg font-medium">{t("editing_reason")}</span>
        </DialogTitle>
        <DialogDescription className="text-base">
          <span className="text-sm">{t("editing_reason_form_text")}</span>
        </DialogDescription>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full items-end gap-4 mt-4">
            <div className="grid grid-cols-1 items-end w-full gap-4">
              <Controller
                name="reason"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative flex flex-col space-y-2">
                    <Label>{t("downtimes")}*</Label>
                    <OneSelector
                      placeholder={"Выберите причину простоя"}
                      value={
                        value
                          ? downtimes.find(
                              (s) => s.label == String(value.label),
                            )!
                          : undefined
                      }
                      onChange={(e) => {
                        onChange(e);
                      }}
                      defaultOptions={downtimes}
                    />
                  </div>
                )}
              />
            </div>
            <div className="flex justify-between mt-4">
              <Button
                disabled={data?.reason?.id ? false : true}
                type="button"
                variant={"destructive"}
                size={"sm"}
                onClick={openDeleteModal}
              >
                {t("delete")}
              </Button>
              <Button variant={"default"} size={"sm"}>
                {t("save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
      <DeleteReason
        id={equipmentId!}
        downtimeId={data?.reason?.id}
        isDeleteOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        refreshChanges={refreshChanges}
        closeModalOnDelete={closeModal}
      />
    </DialogRoot>
  );
};

export default EditLossesReasonsModal;
